'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useDataProvider } from '@effektify/data-provider';
import { GradientDefs } from '@effektify/engine';
import { styled } from '@mui/material/styles';
import { MotionConfig } from 'motion/react';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { FlexColumn } from 'src/components/Atoms/FlexBox';
import { ReportWrapper } from 'src/components/Organisms/ReportWrapper';
import { Wizard } from 'src/components/Organisms/Wizard';
import { KNOWN_FEATURES } from 'src/constants/features';
import { ROUTES } from 'src/constants/routes';
import useFeatures from 'src/hooks/useFeatures';
import useRouteWithParams from 'src/hooks/useRouteWithParams';
import useTeams from 'src/hooks/useTeams';
import useUserDetails from 'src/hooks/useUserDetails';
import HeroAskMeAnything from './HeroAskMeAnything';
import HeroInsights from './HeroInsights';
import HeroLineChart from './HeroLineChart';
import HeroSection from './HeroSection';
import useHeroSlots from './useHeroSlots';
import useHeroState from './useHeroState';
import { humanizeAdapterName } from './utils';
const Content = styled(FlexColumn)(({
  theme
}) => ({
  width: '100%',
  maxWidth: '72rem',
  marginInline: 'auto',
  gap: theme.sizes.spacing['14'],
  paddingBottom: theme.sizes.spacing['15']
}));
const HeroOverview: React.FC = () => {
  const $ = _c(47);
  const {
    report
  } = useDataProvider();
  const {
    userDetails
  } = useUserDetails();
  const {
    currentTeam
  } = useTeams();
  const {
    isEnabled
  } = useFeatures();
  const {
    updatePathWithParams
  } = useRouteWithParams();
  const router = useRouter();
  const slots = useHeroSlots(report);
  const {
    heroState,
    connections,
    adapters,
    isDataSettled
  } = useHeroState(slots);
  const companyId = currentTeam?.id ?? "";
  let t0;
  if ($[0] !== companyId || $[1] !== updatePathWithParams) {
    t0 = updatePathWithParams(ROUTES.CONNECTIONS, {
      companyId
    });
    $[0] = companyId;
    $[1] = updatePathWithParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const connectionsUrl = t0;
  let t1;
  if ($[3] !== companyId || $[4] !== updatePathWithParams) {
    t1 = updatePathWithParams(ROUTES.INSIGHTS, {
      companyId
    });
    $[3] = companyId;
    $[4] = updatePathWithParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const insightsUrl = t1;
  const teamName = currentTeam?.name ?? "Your company";
  let t2;
  if ($[6] !== adapters) {
    t2 = adapters.map(_temp);
    $[6] = adapters;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const platformNames = t2;
  let t3;
  if ($[8] !== router) {
    t3 = url => {
      router.push(url);
    };
    $[8] = router;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleNavigate = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <GradientDefs />;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = userDetails?.first_name ?? "";
  let t6;
  if ($[11] !== connections || $[12] !== connectionsUrl || $[13] !== handleNavigate || $[14] !== heroState || $[15] !== isDataSettled || $[16] !== platformNames || $[17] !== slots || $[18] !== t5 || $[19] !== teamName) {
    t6 = <HeroSection slots={slots} heroState={heroState} connections={connections} platformNames={platformNames} firstName={t5} teamName={teamName} connectionsUrl={connectionsUrl} onNavigate={handleNavigate} isDataSettled={isDataSettled} />;
    $[11] = connections;
    $[12] = connectionsUrl;
    $[13] = handleNavigate;
    $[14] = heroState;
    $[15] = isDataSettled;
    $[16] = platformNames;
    $[17] = slots;
    $[18] = t5;
    $[19] = teamName;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== connectionsUrl || $[22] !== handleNavigate || $[23] !== heroState || $[24] !== isEnabled || $[25] !== teamName) {
    t7 = isEnabled(KNOWN_FEATURES.ASK_ME_ANYTHING, false) && <HeroAskMeAnything heroState={heroState} teamName={teamName} onConnect={() => handleNavigate(connectionsUrl)} />;
    $[21] = connectionsUrl;
    $[22] = handleNavigate;
    $[23] = heroState;
    $[24] = isEnabled;
    $[25] = teamName;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== connectionsUrl || $[28] !== handleNavigate || $[29] !== heroState || $[30] !== isDataSettled || $[31] !== slots.chart) {
    t8 = slots.chart && <HeroLineChart card={slots.chart} heroState={heroState} onConnect={() => handleNavigate(connectionsUrl)} isDataSettled={isDataSettled} />;
    $[27] = connectionsUrl;
    $[28] = handleNavigate;
    $[29] = heroState;
    $[30] = isDataSettled;
    $[31] = slots.chart;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== connections || $[34] !== connectionsUrl || $[35] !== handleNavigate || $[36] !== heroState || $[37] !== insightsUrl) {
    t9 = <HeroInsights heroState={heroState} connections={connections} insightsUrl={insightsUrl} connectionsUrl={connectionsUrl} onNavigate={handleNavigate} />;
    $[33] = connections;
    $[34] = connectionsUrl;
    $[35] = handleNavigate;
    $[36] = heroState;
    $[37] = insightsUrl;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== t6 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t10 = <ReportWrapper>{t4}<Content>{t6}{t7}{t8}{t9}</Content></ReportWrapper>;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Wizard />;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t10) {
    t12 = <MotionConfig reducedMotion="user">{t10}{t11}</MotionConfig>;
    $[45] = t10;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
};
export default HeroOverview;
function _temp(adapter) {
  return humanizeAdapterName(adapter.id);
}